import { Microfrontend } from '@cian/mf-react';
import { useBooleanState } from '@cian/react-utils';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { selectOfferId } from 'shared/selectors';
import { getOfferCustomUrl } from 'shared/selectors/analytics';
import { TDIYAnalyticsClickType } from 'shared/types/microfrontends/newbuildingBrokerModals';

import { NewbuildingBrokerModalsContext } from './NewbuildingBrokerModalsContext';
import { useFallbackModalSettings, useMicrofrontAPI, useMicrofrontEvents, useRegion, useUser } from './hooks';
import { TOpenMicrofrontResultHandler, TPlace } from './types';
import { NewbuildingBrokerFallbackModal } from '../../components/NewbuildingBrokerFallbackModal';

/**
 * Компонент, обеспечивающий загрузку микрофронта модалок бронирования на клиенте.
 *
 * 1. Пользователь кликает на точку входа, и вызывается `startMicrofrontOpening`.
 * 2. Регистрируем манифест, загружаем ассеты
 * 3. Сеттим в React-стейт манифест. Ждем сеттинга с помощью useEffect.
 * 4. По стейту отрисовывается <Microfrontend />
 * 5. Инициализируется реф на <Microfrontend />, содержащий локальное API.
 * 6. Вызываем на рефе локальное API, и открывается модалка.
 */
export const NewbuildingBrokerModalsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const offerId = useSelector(selectOfferId);
  const gaLabel = useSelector(getOfferCustomUrl);
  const user = useUser();
  const region = useRegion();

  const fallbackModalSettings = useFallbackModalSettings();
  const { state: isFallbackModalVisible, setTrue: showFallbackModal, setFalse: closeFallbackModal } = useBooleanState();
  const [loadingPlace, setLoadingPlace] = React.useState<TPlace | null>(null);
  const lastLoadingPlaceRef = React.useRef<TPlace | null>(null);

  const finishMicrofrontOpening = React.useCallback<TOpenMicrofrontResultHandler>(
    ({ isError }) => {
      if (isError) {
        showFallbackModal();
        setLoadingPlace(null);
      } else {
        if (isFallbackModalVisible) {
          closeFallbackModal();
        }

        // снятие loading произойдет по событию из микрофронта.
      }
    },
    [isFallbackModalVisible, showFallbackModal, closeFallbackModal],
  );

  const { openMicrofront, microfrontManifest, microfrontRef } = useMicrofrontAPI(finishMicrofrontOpening);

  useMicrofrontEvents(microfrontManifest, lastLoadingPlaceRef, setLoadingPlace);

  // Реф нужен, чтобы получать свежего user в коллбэке во избежание проблемы с замыканием на старое значение.
  const userRef = React.useRef(user);

  React.useEffect(() => {
    userRef.current = user;
  }, [user]);

  const startMicrofrontOpening = React.useCallback(
    (place: TPlace) => {
      setLoadingPlace(place);

      if (place !== 'fallbackModal') {
        lastLoadingPlaceRef.current = place;
      }

      openMicrofront({
        user: userRef.current,
        region,
        gaLabel,
        requestType: 'offer',
        offerId,
        analyticsClickType: lastLoadingPlaceRef.current as TDIYAnalyticsClickType,
      });
    },
    [region, gaLabel, offerId, openMicrofront],
  );

  const handleRetry = React.useCallback(() => {
    startMicrofrontOpening('fallbackModal');
  }, [startMicrofrontOpening]);

  const value = React.useMemo(
    () => ({
      loadingPlace,
      startMicrofrontOpening,
    }),
    [loadingPlace, startMicrofrontOpening],
  );

  return (
    <NewbuildingBrokerModalsContext.Provider value={value}>
      {children}
      {isFallbackModalVisible && (
        <NewbuildingBrokerFallbackModal
          {...fallbackModalSettings}
          loading={Boolean(loadingPlace)}
          onClose={closeFallbackModal}
          onRetry={handleRetry}
        />
      )}
      {microfrontManifest && (
        <Microfrontend
          ref={microfrontRef}
          name={microfrontManifest.microfrontendName}
          image={microfrontManifest.imageVersion}
          runtime={microfrontManifest.runtimeName}
          instance={microfrontManifest.instanceId}
        />
      )}
    </NewbuildingBrokerModalsContext.Provider>
  );
};
